import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppUpdateService } from './services/app-update.service';
import { ChatClientService } from './services/chat-client.service';
import { LangService } from './services/lang.service';
import { PushNotifyService } from './services/push-notify.service';
import { PWAInstallService } from './services/pwa-install.service';
import { SharedService } from './services/shared.service';
import {
  getOrganizationSettings,
  getUserInfo,
} from './store/auth/auth.actions';
import { selectAuth } from './store/auth/auth.selector';
import { TokenAuthState } from './store/auth/auth.state';
import { AutoUnsubscribeComponent } from './utility/auto-unsubscribe.component';
import { getTokenFromStorage, showError } from './utility/common-logic';
import { clearLocalStorage } from './utility/local-storage';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends AutoUnsubscribeComponent implements OnInit {
  app = environment.settings.app;
  applyBgColorFromDjango = environment.settings.applyBgColorFromDjango;
  isPushNotificationSupport = environment.settings.isPushNotificationSupport;
  freshchat = environment.settings.freshchat;
  isSiteUnderMaintenance = environment.settings.isSiteUnderMaintenance ?? false;
  auth$: TokenAuthState;
  isInIframe: boolean = false;
  currentRoute: string = '';

  constructor(
    private push: PushNotifyService,
    public shared: SharedService,
    public lang: LangService,
    public translate: TranslateService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private store: Store,
    private ngZone: NgZone,
    private router: Router,
    public pwaInstallService: PWAInstallService,
    private toastr: ToastrService,
    private appUpdate: AppUpdateService,
    public chatClientService: ChatClientService,
  ) {
    super();
  }

  ngOnInit() {
    void this.appUpdate.init().catch((err) => {
      showError(err, this.toastr, this.translate);
    });

    this.isInIframe = this.shared.isInIframe();

    const hasToken = this.hasValidToken();
    if (this.isInIframe) {
      clearLocalStorage();
      document.body.classList.add('is-in-iframe');
      const hash = window.location.hash || '';
      const route = hash.replace(/^#\//, '') || 'home';

      if (!hasToken) {
        this.requestTokenFromParent();
      }

      window.addEventListener('message', (event) => {
        this.ngZone.run(() => {
          const data = event.data;
          if (typeof data === 'string' && data.startsWith('UPEXTERNAL=')) {
            const match = data.match(/^UPEXTERNAL=([^;]+)/);

            if (match && match[1]) {
              const token = match[1];

              if (token === 'noTokenAvailable') {
                this.requestTokenFromParent();
              } else {
                this.shared.iframeTokenDetails.next({
                  session_token: token,
                  redirect_url: route,
                });
                this.router.navigate(['/iframe-redirect']);
              }
            } else {
              showError(
                this.translate.instant('msg.error.invalidTokenFromParent'),
                this.toastr,
                this.translate,
                true,
              );
              this.router.navigate(['/login']);
            }
          } else {
            showError(
              this.translate.instant('msg.error.invalidCookieFromParent'),
              this.toastr,
              this.translate,
              true,
            );
            this.router.navigate(['/login']);
          }
        });
      });
    }

    this.store
      .pipe(select(selectAuth), takeUntil(this.destroy$))
      .subscribe((auth) => {
        this.auth$ = auth;
      });

    const authToken = getTokenFromStorage();
    if (!!authToken) {
      // Call these API if Token exists
      this.store.dispatch(getUserInfo());
      this.store.dispatch(getOrganizationSettings());
    }

    document.body.setAttribute('id', this.app);

    if (this.isPushNotificationSupport && this.shared.isLogin()) {
      this.push.subscribeToNotifications();
      this.push.showMessages();
    }

    if (this.lang.isRTL) {
      document.body.setAttribute('dir', 'rtl');
      const htmlElement = document.getElementsByTagName('html')[0];
      htmlElement.setAttribute('dir', 'rtl');
    }

    if (this.freshchat.isShow) {
      this.initFreshChat();
    }

    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target;
      const clickedInsideChatPanel =
        target instanceof Element && !!target.closest('.box-chat-client');

      if (
        this.chatClientService.isEnabled &&
        this.chatClientService.isPanelOpen &&
        !this.chatClientService.openingPanel &&
        !clickedInsideChatPanel
      ) {
        this.chatClientService.close();
        return;
      }

      if (!this.chatClientService.isEnabled) {
        const launcher =
          this.elementRef.nativeElement.querySelector('.hotline-launcher');
        if (launcher == null) {
          (window as any).fcWidget?.close();
        }
      }
    });
  }

  hasValidToken(): boolean {
    const cookieToken = this.getCookieValue();
    return !!cookieToken && cookieToken !== 'noTokenAvailable';
  }

  getCookieValue(): string | null {
    const cookie = document.cookie.trim();
    if (cookie.startsWith('UPEXTERNAL=')) {
      return cookie.substring('UPEXTERNAL='.length) || null;
    }
    return null;
  }

  requestTokenFromParent(): void {
    window.parent.postMessage('retrieveToken', '*');
  }

  showFreshChatLauncherIfConfigured(): void {
    const fcWidget = (window as any).fcWidget;
    if (!fcWidget) {
      return;
    }

    if (this.freshchat.pages === 'all' || this.freshchat.pages === 'support') {
      fcWidget.show();
    } else {
      fcWidget.hide();
    }
  }

  initFreshChat() {
    const authData = this.auth$;

    try {
      (window as any).fcWidgetMessengerConfig = {
        open: false,
        tags: environment.settings.freshchat.tags,
        config: {
          headerProperty: {
            appName: environment.settings.appName,
            // direction: 'rtl',
            hideChatButton: environment.settings.freshchat.chat,
          },
          content: {
            welcomeMessage: 'Chat with Us',
            welcomeSubMessage: 'Solution Articles',
          },
          disableEvents: true,
          eagerLoad: false,
        },
      };

      (window as any).fcSettings = {
        onInit: () => {
          const fcWidget = (window as any).fcWidget;
          if (!fcWidget) {
            return;
          }

          fcWidget.setExternalId(authData?.user_details?.email);
          fcWidget.user.setFirstName(authData?.user_details?.first_name);
          fcWidget.user.setLastName(authData?.user_details?.last_name);
          fcWidget.user.setEmail(authData?.user_details?.email);
          fcWidget.user.setProperties({
            organization: authData?.organization_details?.slug,
          });

          this.chatClientService.wireFreshChatLauncher(fcWidget, this.ngZone);

          if (!this.chatClientService.isEnabled) {
            this.showFreshChatLauncherIfConfigured();
          }
        },
      };

      const scriptSrc = environment.settings.freshchat.script;
      const hasScript = Array.from(
        document.querySelectorAll('script[chat="true"]'),
      ).some((node) => node.getAttribute('src') === scriptSrc);

      if (!hasScript) {
        const chatElement = document.createElement('script');
        chatElement.setAttribute('src', scriptSrc);
        chatElement.setAttribute('chat', 'true');
        document.body.appendChild(chatElement);
      }
    } catch (error) {}
  }
}
